import { useLang } from "../i18n/LanguageContext";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/9e1313c0-9298-432e-9d95-61137a646d40/images/52d1e1c3ef621b13143dd25f12311d61ed8036c8ba4448c0375f6d2822d422be.png";

export const Hero = () => {
  const { t } = useLang();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Top meta line */}
        <div className="flex items-center justify-between mb-12 md:mb-16 fade-up">
          <span className="overline" data-testid="hero-overline">
            {t.hero.overline}
          </span>
          <span className="hidden md:flex items-center gap-3 font-mono-utility text-[10px] tracking-[0.25em] uppercase text-[#5b664f]">
            <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-[#a64b2a]" />
            {t.hero.status_label}
          </span>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          {/* Headline */}
          <div className="col-span-12 lg:col-span-7">
            <h1
              data-testid="hero-title"
              className="font-serif-display text-[14vw] sm:text-[10vw] lg:text-[7.5vw] xl:text-[6.5rem] leading-[0.95] tracking-tighter text-[#2c1e16]"
            >
              <span className="block fade-up">{t.hero.title_a}</span>
              <span className="block italic text-[#a64b2a] fade-up fade-up-delay-1">
                {t.hero.title_b}
              </span>
              <span className="block fade-up fade-up-delay-2">{t.hero.title_c}</span>
            </h1>
          </div>

          {/* Image */}
          <div className="col-span-12 lg:col-span-5 fade-up fade-up-delay-3">
            <div className="zoom-frame relative rounded-sm overflow-hidden border border-[#e5d9c5]">
              <img
                src={HERO_IMAGE}
                alt="Afro-Swiss soul food plated dish"
                className="w-full h-[360px] md:h-[460px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-[10px] font-mono-utility tracking-widest uppercase text-[#f9f6f0]/90">
                <span className="bg-[#2c1e16]/70 backdrop-blur px-2 py-1 rounded-sm">
                  {t.hero.plate_label}
                </span>
                <span className="bg-[#2c1e16]/70 backdrop-blur px-2 py-1 rounded-sm">
                  {t.hero.city_label}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Sub + CTAs */}
        <div className="grid grid-cols-12 gap-6 md:gap-10 mt-12 md:mt-16">
          <div className="col-span-12 lg:col-span-5 lg:col-start-2">
            <p
              data-testid="hero-sub"
              className="text-lg md:text-xl text-[#2c1e16]/80 leading-relaxed font-light fade-up fade-up-delay-3"
            >
              {t.hero.sub}
            </p>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex flex-col sm:flex-row gap-3 sm:items-center fade-up fade-up-delay-4">
            <a
              href="#boarding"
              data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-2 bg-[#2c1e16] text-[#f9f6f0] px-7 py-4 rounded-full text-sm tracking-wider uppercase font-mono-utility hover:bg-[#a64b2a] transition-colors"
            >
              {t.hero.cta_primary}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a
              href="#story"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 border border-[#2c1e16]/30 text-[#2c1e16] px-7 py-4 rounded-full text-sm tracking-wider uppercase font-mono-utility hover:border-[#2c1e16] transition-colors"
            >
              {t.hero.cta_secondary}
              <ArrowDown className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="mt-20 md:mt-28 border-y border-[#e5d9c5] bg-[#f2ebe1]/60 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap py-4">
          {Array.from({ length: 2 }).map((_, k) => (
            <div
              key={k}
              className="flex items-center gap-10 px-6 font-serif-display text-3xl md:text-4xl italic text-[#2c1e16]/70"
              aria-hidden={k === 1}
            >
              {t.hero.marquee.split("·").map((piece, i) => (
                <span key={i} className="flex items-center gap-10">
                  <span>{piece.trim()}</span>
                  {i < t.hero.marquee.split("·").length - 1 && (
                    <span className="text-[#a64b2a]">✦</span>
                  )}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
